<template>
  <DetailLayoutPage
    :title="title"
    :lead="lead"
    :body="body"
    :is-sustainable
    :features
    :nid-nearby="nid"
    :nid-related="nid"
    :address="address"
    :opening-hours="addressOpeningHours"
    :zurich-card-benefit="zurichCardBenefit"
    :nid="nid"
    :key-visual="keyVisual"
    :images="mediaSlider"
    :review-url
    :special-offers="filterNullArray(specialOffers)"
  >
    <template v-if="ctaLinks.length" #cta>
      <DetailLayoutActionLinks :links="ctaLinks" />
    </template>

    <template v-if="nid" #events>
      <ClientOnly>
        <DetailLayoutEvents :nid="nid" :same-area-pois="sameAreaPois" />
      </ClientOnly>
    </template>

    <template v-if="priceDescription" #price>
      <div class="content" v-html="priceDescription" />
    </template>

    <template #content>
      <div v-if="maximumCapacity">
        <h3 class="title is-h5">
          {{ $texts('maximumCapacity') }}
        </h3>
        <p itemprop="maximumAttendeeCapacity">{{ maximumCapacity }}</p>
      </div>
    </template>
    <template v-if="remoteVideo" #video>
      <MediaRemoteVideo v-bind="remoteVideo" />
    </template>
    <template v-if="panorama || panoramaPictureUrl" #panorama>
      <LunchgatePanorama v-if="panorama" :url="panorama" />
      <PanoramaScroller v-if="panoramaPictureUrl" :image="panoramaPictureUrl" />
    </template>

    <template v-if="matterportId" #virtualTour>
      <MatterportShowcase v-if="matterportId" :matterport-id="matterportId" />
    </template>

    <template v-if="showOpeningHours && addressOpeningHours" #openingHours>
      <AddressOpeningHours
        v-if="addressOpeningHours"
        v-bind="addressOpeningHours"
        :nid="nid"
      >
        <Weekdays :open="weekdaysOpen" />
      </AddressOpeningHours>
    </template>

    <template v-if="address" #address>
      <AddressCard :address="address" :title="title" :nid="nid" />
    </template>

    <template v-if="nid" #below>
      <TeaserQueryNearby
        v-if="activeDomain !== DomainId.meeting_zuerich_com"
        :nid="nid"
        :classifications="placeClassifications"
      />
    </template>
  </DetailLayoutPage>
</template>

<script lang="ts" setup>
import {
  DomainId,
  type MediaImageFragment,
  type NodePlaceFragment,
} from '#graphql-operations'
import { notNullish } from '@vueuse/core'
import type { ActionLink } from '~/components/DetailLayout'
import { filterNullArray } from '~/helpers'
import { getCantoImageStyle } from '~/helpers/canto'

const { $texts } = useEasyTexts()

const { activeDomain } = useSiteContext()

const props = defineProps<NodePlaceFragment>()

const features = computed(() =>
  props.details
    ?.map((text) => {
      if (text) {
        return { text }
      }
      return null
    })
    .filter(notNullish),
)

const language = useCurrentLanguage()

const zurichCardBenefit = computed(() => props.zurichCard?.[0]?.name)

const showOnlineBooking = computed(
  () => props.showBookingWidget && !!props.tomasBookingId,
)

const ctaLinks = computed(() => {
  const links: ActionLink[] = filterNullArray(
    props.actionLinks?.map((v) => {
      if (!v) {
        return null
      }
      return {
        title: v.title || '',
        url: v.url?.path || '',
        target: '_blank',
      }
    }),
  )

  if (props.webform) {
    const url = props.webform.url?.path
    if (url) {
      links.push({
        title: $texts('requestVenue'),
        url: url + `?source_entity_id=${props.nid}&source_entity_type=node`,
      })
    }
  }

  if (showOnlineBooking.value) {
    links.unshift({
      title: $texts('buyTickets'),
      url: '/' + language.value + '/buy-tickets/' + props.nid,
      classes: 'track-buy-tickets',
    })
  }

  if (links && links.length) {
    return links
  }

  return []
})

const isVenue = computed(() => !!props.venueType?.length)

const weekdaysOpen = computed(
  () => props.weekdays?.map((v) => v?.tid).filter(notNullish) || [],
)

const showOpeningHours = computed(() => {
  if (isVenue.value) {
    return false
  }
  if (weekdaysOpen.value.length) {
    return true
  }
  if (props.addressOpeningHours) {
    const v = props.addressOpeningHours
    if (v.alwaysOpen || v.structured?.length || v.currentlyClosed || v.custom) {
      return true
    }
  }
  return false
})

const panoramaPictureUrl = computed(
  () =>
    (props.panoramaPicture as MediaImageFragment | undefined)?.fileUrl?.path,
)

const { amenityFeatures: accessibilityFeatures } = await useAccessibilityInfo(
  () => props.nid,
)

const schemaOrg = computed(() => {
  const amenityFeatures = [
    ...(features.value?.map((feature) => ({
      '@type': 'LocationFeatureSpecification',
      name: feature.text,
      value: true,
    })) ?? []),
    ...accessibilityFeatures.value,
  ]
  const address = props.address
  const drupalImage =
    props.keyVisual && 'image' in props.keyVisual
      ? props.keyVisual.fileUrl
      : undefined
  const cantoImage =
    props.keyVisual && 'assetId' in props.keyVisual
      ? getCantoImageStyle(props.keyVisual.assetId!, {
          width: 1280,
          height: 960,
        })
      : undefined

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Place',
        name: props.title,
        image: drupalImage?.path || cantoImage?.url || undefined,
        url: address?.website?.url?.path || undefined,
        telephone: address?.phone || undefined,
        address: {
          '@type': 'PostalAddress',
          description: address?.address?.organization || undefined,
          streetAddress: address?.address?.addressLine1 || undefined,
          postalCode: address?.address?.postalCode || undefined,
          addressLocality: address?.address?.locality || undefined,
          // addressRegion: address?.address?.|| undefined,
          addressCountry: {
            '@type': 'Country',
            name: address?.address?.countryCode || undefined,
          },
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: address?.location?.lat || undefined,
          longitude: address?.location?.lon || undefined,
        },
        amenityFeature: amenityFeatures,
      },
    ],
  }
})

useHead(
  computed(() => ({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(schemaOrg.value),
      },
    ],
  })),
)
</script>
